// material-ui
import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// api
import { IRealtimeAnalytics } from '~/src/dash/api/console-api';
// components
import { ISelectedMetrics } from '~/src/dash/components/live-data/live-data';
import { LiveDataResourceType } from '~/src/dash/components/live-data/live-data';
import { LiveDataGraphTooltip } from '~/src/dash/components/live-data/live-data-graph-tooltip';
const {
  AGG_STREAM_AD_COMPLETE_KEY,
  AGG_STREAM_AD_FILLED_KEY,
  AGG_STREAM_AD_IMPRESSION_KEY,
  AGG_STREAM_CHUNKLIST_RESPONSE_KEY,
  AGG_STREAM_POD_REQUEST_KEY,
  AGG_STREAM_START_KEY,
} = networkHomeAggDataKeys;
import { IHistoryProps } from '~/src/dash/types/props';
// views
import { LoadingStateHasData } from '~/src/dash/utils/loading-states';
import { networkHomeAggDataKeys } from '~/src/dash/views/networks/constants';

interface IProps extends IHistoryProps {
  classes: { [key: string]: string };
  selectedMetrics: ISelectedMetrics;
  stackedBarMetrics?: { [key: string]: string[] };
  selectedIDs: string[];
  loaded: number | boolean;
  LoadingStateHasData: string | number;
  // data to display in the graph
  data1: IRealtimeAnalytics[];
  graphTitle: string;
  currencySymbol: string;
  resourceType: LiveDataResourceType;
  publisherID: string;
}

const checkKeysForString = (keys: string[], targets: string[]): boolean => {
  for (const target of targets) {
    if (keys.includes(target)) {
      return true;
    }
  }
  return false;
};

const stackedBarColors = ['#4952FF', '#D30C7B', '#A34FC2', '#8377D1', '#837AAA', '#FBDD62'];

export const LiveDataGraph = (props: IProps) => {
  const {
    classes,
    selectedMetrics,
    graphTitle,
    loaded,
    data1,
    selectedIDs,
    currencySymbol,
    resourceType,
    stackedBarMetrics,
    location,
    history,
    publisherID,
  } = props;

  // need legend for resourceTypes with stackedBar metrics
  const needLegend = resourceType === 'InventoryRoute' ? true : false;

  const graphs = [],
    tooltipHeaders = [];
  let bigYAxisNeeded,
    mediumYAxisNeeded,
    smallYAxisNeeded,
    bidResponseAxisNeeded,
    bidRequestAxisNeeded,
    errorAxisNeeded;
  const smallYAxisKeys = [
    // sites
    'CPM',
    'BidderLatency',
    'BidTimeout',
    'BidError',
    // stream
    'StreamAdChunklistResponse',
    // campaign
    'AdserverImpression',
    'AdserverComplete',
    'DestinationLatency',
    // automated bid floor
    'BidFloorOptimized',
    'BidFloorHigh',
    'BidFloorLow',
  ];
  const bigYAxisKeys = [
    // sites
    'EndpointRequests',
    'Impressions',
    'WinningBids',
    // stream
    AGG_STREAM_START_KEY,
    AGG_STREAM_POD_REQUEST_KEY,
    AGG_STREAM_AD_FILLED_KEY,
    AGG_STREAM_AD_IMPRESSION_KEY,
    AGG_STREAM_AD_COMPLETE_KEY,
    // campaign
    'AdserverRequest',
    'AdserverFilled',
    'AdserverInventoryRequest',
    'AdserverIdealImpressionCount',
    'AdserverPacedRequestTest',
    'AdserverImpressionTest',
    // inventory routing
    'DestinationRequests',
    'DestinationResponses',
    // automated bid floor
    'BidRequestsOptimized',
    'BidRequestsLow',
    'BidRequestsHigh',
    'BidResponsesOptimized',
    'BidResponsesLow',
    'BidResponsesHigh',
    'CPMOptimized',
    'CPMHigh',
    'CPMLow',
    'WinningBidsOptimized',
    'WinningBidsHigh',
    'WinningBidsLow',
  ];
  const ErrorAxisKeys = ['DestinationErrors', 'DestinationNoFills', 'DestinationTimeouts'];
  const isLatencyGraph = checkKeysForString(Object.keys(selectedMetrics), [
    'EndpointLatency',
    'AuctionLatency',
    'BidderLatency',
  ]);
  const isBidErrorGraph = checkKeysForString(Object.keys(selectedMetrics), [
    'BidError',
    'BidTimeout',
  ]);
  // axis count to see how many axis are needed for graph
  let axisCount = 0;
  if (checkKeysForString(Object.keys(selectedMetrics), ['Revenue', 'EstimatedRevenueOptimized'])) {
    mediumYAxisNeeded = true;
    axisCount++;
  }
  if (checkKeysForString(Object.keys(selectedMetrics), smallYAxisKeys)) {
    smallYAxisNeeded = true;
    axisCount++;
  }
  if (checkKeysForString(Object.keys(selectedMetrics), bigYAxisKeys)) {
    bigYAxisNeeded = true;
    axisCount++;
  }
  if (checkKeysForString(Object.keys(selectedMetrics), ['BidRequest'])) {
    bidRequestAxisNeeded = true;
    axisCount++;
  }
  if (checkKeysForString(Object.keys(selectedMetrics), ['BidResponse'])) {
    bidResponseAxisNeeded = true;
    axisCount++;
  }
  if (checkKeysForString(Object.keys(selectedMetrics), ErrorAxisKeys)) {
    errorAxisNeeded = true;
    axisCount++;
  }
  // priority will go to medium revenue axis, small axis is for CPM, latency
  // big axis is for impressions, endpoint requests, auction inits and winning bids
  // bidRequest and bidResponse axis are extra large and have low priority
  const bigYAxis = (
    <YAxis
      yAxisId='big'
      orientation={
        smallYAxisNeeded && mediumYAxisNeeded
          ? 'right'
          : smallYAxisNeeded || (!smallYAxisNeeded && !mediumYAxisNeeded)
          ? 'left'
          : 'right'
      }
      scale='auto'
      domain={[0, 'auto']}
      unit={''}
      hide={loaded !== LoadingStateHasData || !bigYAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
      tick={{ fontSize: '10px' }}
    />
  );
  const mediumYAxis = (
    <YAxis
      yAxisId='medium'
      orientation='left'
      scale='auto'
      domain={[0, 'auto']}
      unit={'$'}
      hide={loaded !== LoadingStateHasData || !mediumYAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
      tick={{ fontSize: '10px' }}
    />
  );
  const smallYAxis = (
    <YAxis
      yAxisId='small'
      orientation={!bigYAxisNeeded && !mediumYAxisNeeded ? 'left' : 'right'}
      scale='auto'
      domain={[0, 'auto']}
      unit={''}
      hide={
        loaded !== LoadingStateHasData || (bigYAxisNeeded && mediumYAxisNeeded) || !smallYAxisNeeded
      }
      tickFormatter={(value: number) => value.toLocaleString()}
      tick={{ fontSize: '10px' }}
    />
  );
  const bidRequestAxis = (
    <YAxis
      yAxisId='bidRequest'
      // only left orientation if only axis
      orientation={axisCount === 1 && bidRequestAxisNeeded ? 'left' : 'right'}
      scale='auto'
      domain={[0, 'auto']}
      unit={''}
      // this axis has low priority and should only show if there are 2 axis or less
      hide={loaded !== LoadingStateHasData || axisCount > 2 || !bidRequestAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
      tick={{ fontSize: '10px' }}
    />
  );
  const bidResponseAxis = (
    <YAxis
      yAxisId='bidResponse'
      // only on left orientation if it is the only axis or the only other is bidRequest
      orientation={
        (axisCount === 2 && bidRequestAxisNeeded) || (axisCount === 1 && bidResponseAxisNeeded)
          ? 'left'
          : 'right'
      }
      scale='auto'
      domain={[0, 'auto']}
      unit={''}
      // this axis has low priority and should only show if there are 2 axis or less unless the third axis is bidRequest
      hide={
        loaded !== LoadingStateHasData ||
        (axisCount === 3 && !bidRequestAxisNeeded) ||
        axisCount > 3 ||
        !bidResponseAxisNeeded
      }
      tickFormatter={(value: number) => value.toLocaleString()}
      tick={{ fontSize: '10px' }}
    />
  );
  const errorAxis = (
    <YAxis
      yAxisId='error'
      orientation={!bigYAxisNeeded && !mediumYAxisNeeded && !smallYAxisNeeded ? 'left' : 'right'}
      scale='auto'
      domain={[0, 'auto']}
      unit={''}
      // this axis has low priority and should only show if there are 2 axis or less unless the third axis is bidRequest
      hide={loaded !== LoadingStateHasData || axisCount > 2 || !errorAxisNeeded}
      tickFormatter={(value: number) => value.toLocaleString()}
      tick={{ fontSize: '10px' }}
    />
  );

  // Check if this is the latency graph, if so, only small axis is needed.
  // If you want to change the metrics order, change it here.
  let yAxes = isLatencyGraph
    ? [smallYAxis]
    : isBidErrorGraph
    ? [bigYAxis, smallYAxis]
    : [bigYAxis, smallYAxis, mediumYAxis, bidRequestAxis, bidResponseAxis, errorAxis];

  // automated bid floor graph will always show bid floor price, so small yAxis is always needed on right orientation
  yAxes =
    resourceType === 'AutomatedBidFloor'
      ? [
          <YAxis
            yAxisId='big'
            orientation='left'
            scale='auto'
            domain={[0, 'auto']}
            unit={''}
            hide={loaded !== LoadingStateHasData || mediumYAxisNeeded}
            tickFormatter={(value: number) => value.toLocaleString()}
            tick={{ fontSize: '10px' }}
          />,
          <YAxis
            yAxisId='medium'
            orientation='left'
            scale='auto'
            domain={[0, 'auto']}
            unit={'$'}
            hide={loaded !== LoadingStateHasData || !mediumYAxisNeeded}
            tickFormatter={(value: number) => value.toLocaleString()}
            tick={{ fontSize: '10px' }}
          />,
          <YAxis
            yAxisId='small'
            orientation='right'
            scale='auto'
            domain={[0, 'auto']}
            unit={'$'}
            hide={loaded !== LoadingStateHasData}
            tickFormatter={(value: number) => value.toLocaleString()}
            tick={{ fontSize: '10px' }}
          />,
        ]
      : yAxes;

  // iterate over the selectedMetrics and create the line / bar
  // add all bars first so they are "behind" the lines
  for (const [key, selectedMetric] of Object.entries(selectedMetrics)) {
    if (selectedMetric.graphType === 'Bar') {
      tooltipHeaders.push(<Grid>{key}</Grid>);
      graphs.push(
        <Bar
          maxBarSize={80}
          dataKey={key}
          fill={selectedMetric.color}
          yAxisId={selectedMetric.yAxisId}
        />,
      );
      // if element type is a StackedBar, must add a <Bar /> for each child of the bar and assign it a unique color
    } else if (
      stackedBarMetrics &&
      stackedBarMetrics[key] &&
      selectedMetric.graphType === 'StackedBar'
    ) {
      stackedBarMetrics[key].forEach((metric, idx) => {
        tooltipHeaders.push(<Grid>{key}</Grid>);
        graphs.push(
          <Bar
            maxBarSize={80}
            dataKey={metric}
            fill={stackedBarColors[idx]}
            yAxisId={selectedMetric.yAxisId}
            stackId={key}
          />,
        );
      });
    }
  }

  for (const [key, selectedMetric] of Object.entries(selectedMetrics)) {
    // if stream start or chunklist response, do not display if network channel ids are selected
    if (
      (key === AGG_STREAM_START_KEY || key === AGG_STREAM_CHUNKLIST_RESPONSE_KEY) &&
      selectedIDs.length > 0
    ) {
      continue;
    }
    // only show BidFloorOptimized in the graph
    if (key === 'BidFloorLow' || key === 'BidFloorHigh') {
      continue;
    }
    if (selectedMetric.graphType === 'Line') {
      tooltipHeaders.push(<Grid>{key}</Grid>);
      graphs.push(
        <Line
          dot={false}
          type='monotone'
          dataKey={key}
          stroke={selectedMetric.color}
          yAxisId={selectedMetric.yAxisId}
          strokeWidth={4}
        />,
      );
    }
  }

  return (
    <Grid container>
      {graphTitle && (
        <Grid xs={2} item>
          <Typography className={classes.chartName}>{graphTitle}</Typography>
        </Grid>
      )}
      <ResponsiveContainer width='99%' height={256}>
        <ComposedChart data={data1} height={256} margin={{ top: 8, right: 8, left: 8 }}>
          <XAxis
            dataKey={'Date'}
            angle={-45}
            textAnchor='end'
            height={56}
            tick={{ fontSize: '10px' }}
          />
          {yAxes}
          <Tooltip
            wrapperStyle={{ width: 300, zIndex: 1 }}
            filterNull={false}
            content={
              <LiveDataGraphTooltip
                selectedMetrics={selectedMetrics}
                selectedIDs={selectedIDs}
                resourceType={resourceType}
                publisherID={publisherID}
                needLegend={needLegend}
                loaded={loaded}
                currencySymbol={currencySymbol}
                location={location}
                history={history}
              />
            }
          />
          {graphs}
        </ComposedChart>
      </ResponsiveContainer>
    </Grid>
  );
};
